import { forwardRef, ForwardRefRenderFunction, useEffect, useMemo, useState } from "react";
//#region types
import { BenefitsEnums } from "models/enums";
import { BenefitFormTypes } from "models/formTypes";
import { CarBenefitData } from "models/formTypes/benefits";
import { BenefitCreateFormContainerProps } from "models/propTypes/benefitsPropTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { TranslationKeys } from "translations";
import { FormFieldConstants } from "utils/constants";
//#endregion
//#region components
import BenefitCreateForm from "./benefitCreateForm";
import { useDeviationPermissions } from "components/salaryDeviation";
import { useBenefitService, useWintSalaryService } from "serviceHooks";
import { LoadingWithLogo } from "components/common";
import useBenefitTypesHelper from "./useBenefitTypesHelper";
//#endregion
import * as Yup from "yup";
import cssExports from "theme/styles/main.module.scss";

const BenefitCreateFormContainer: ForwardRefRenderFunction<FormHandles, BenefitCreateFormContainerProps> = (
  props,
  ref
) => {
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<AutocompleteListItem[]>([]);
  const [yearList, setYearList] = useState<AutocompleteListItem[]>([]);
  const [brandList, setBrandList] = useState<AutocompleteListItem[]>([]);

  const { isSalaryAdmin, isBusinessUnitManagerWithSalaryRights, isDeviationAdmin } = useDeviationPermissions();
  const { searchPersonsAutoComplete } = useWintSalaryService();
  const { createBenefitReapprove, getYearAndBrandLists } = useBenefitService();

  const { frequencyChoices, benefitTypes } = useBenefitTypesHelper();
  const [initialValues] = useState<BenefitFormTypes.BenefitCreate>({
    frequency: BenefitsEnums.BenefitFrequency.Monthly,
    carBenefitData: {
      searchMethod: BenefitsEnums.CarBenefitSearchMethod.ModelNumber,
      isAnnual10KmMileageMoreThan3000: false,
    },
  });

  useEffect(() => {
    const loadEmployees = async () => {
      setLoading(true);
      try {
        const employees = await searchPersonsAutoComplete(
          "",
          false,
          true,
          isBusinessUnitManagerWithSalaryRights && !(isSalaryAdmin || isDeviationAdmin)
        );
        setEmployees(employees);
        const [yearList, brandList] = await getYearAndBrandLists();
        setYearList(yearList);
        setBrandList(brandList);
      } catch (error) {
        console.error("Failed to load employees:", error);
      } finally {
        setLoading(false);
      }
    };
    loadEmployees();
  }, []);

  const validationSchema = useMemo(() => {
    return Yup.object<BenefitFormTypes.BenefitCreate>({
      employees: Yup.array()
        .of(Yup.object<AutocompleteListItem>())
        .min(1, TranslationKeys.Validations.Required)
        .required(TranslationKeys.Validations.Required),
      benefitType: Yup.object<AutocompleteListItem>().required(TranslationKeys.Validations.Required),
      name: Yup.string().required(TranslationKeys.Validations.Required),
      frequency: Yup.mixed()
        .oneOf(Object.values(BenefitsEnums.BenefitFrequency))
        .required(TranslationKeys.Validations.Required),
      benefitValue: Yup.number()
        .transform((value, originalValue) => {
          // Allow incomplete number inputs like "-", "+" without validation errors
          if (originalValue === "" || originalValue === "-" || originalValue === "+") {
            return undefined;
          }
          return value;
        })
        .required(TranslationKeys.Validations.Required),
      startDate: Yup.date().required(TranslationKeys.Validations.Required),
      endDate: Yup.date(),
      carBenefitData: Yup.object<CarBenefitData>().when(
        FormFieldConstants.Benefits.BenefitBase.BenefitType,
        ([benefitType], schema) => {
          if (
            (benefitType?.id as BenefitsEnums.BenefitType) === BenefitsEnums.BenefitType.Car ||
            (benefitType?.id as BenefitsEnums.BenefitType) === BenefitsEnums.BenefitType.CarNetDeduction
          ) {
            return schema.shape({
              searchMethod: Yup.mixed()
                .oneOf(Object.values(BenefitsEnums.CarBenefitSearchMethod))
                .required(TranslationKeys.Validations.Required),
              modelNumber: Yup.string().required(TranslationKeys.Validations.Required),
              costOfExtraEquipment: Yup.number().required(TranslationKeys.Validations.Required),
              annualTax: Yup.number().required(TranslationKeys.Validations.Required),
              isAnnual10KmMileageMoreThan3000: Yup.boolean().required(TranslationKeys.Validations.Required),
              dateOfRegistration: Yup.date()
                .typeError(TranslationKeys.Validations.InvalidDate)
                .required(TranslationKeys.Validations.Required),
            });
          }
          return schema;
        }
      ),
    });
  }, []);

  const handleSubmit = async (values: BenefitFormTypes.BenefitCreate) => {
    const result = await createBenefitReapprove(values);
    return result.data;
  };

  return (
    <>
      {loading && (
        <div className={cssExports.dialogLoadingContainer}>
          <LoadingWithLogo />
        </div>
      )}
      <BenefitCreateForm
        ref={ref}
        {...props}
        defaultValues={initialValues}
        validationSchema={validationSchema}
        frequencyChoices={frequencyChoices}
        benefitTypes={benefitTypes}
        employees={employees}
        yearList={yearList}
        brandList={brandList}
        handleSubmit={handleSubmit}
      />
    </>
  );
};

export default forwardRef(BenefitCreateFormContainer);
